import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Zap, ShieldCheck, Loader2, Info } from "lucide-react";

export const LANE_META = {
  ready: { label: "Bereit", cls: "border-emerald-300 bg-emerald-50 text-emerald-800", order: 2 },
  review: { label: "Prüfen", cls: "border-amber-300 bg-amber-50 text-amber-800", order: 1 },
  blocked: { label: "Blockiert", cls: "border-rose-300 bg-rose-50 text-rose-800", order: 0 },
};

/** Routing-Grund pro Beleg («Warum nicht automatisch?»). */
export function RoutingBadge({ item }) {
  const r = item.routing;
  if (!r) return null;
  const meta = LANE_META[r.lane] || LANE_META.review;
  const title = r.eligible
    ? (r.would_autobook ? "Erfüllt alle Auto-Kriterien – würde automatisch verbucht (Automatik ist AUS)" : "Erfüllt alle Auto-Kriterien")
    : `Nicht automatisch: ${r.reasons.join(" · ")}`;
  return (
    <span className={`inline-flex items-center gap-1 rounded border px-1.5 py-px text-[10px] font-medium ${meta.cls}`} title={title} data-testid={`inbox-routing-${item.id}`}>
      <Zap className="h-3 w-3" /> {meta.label}{!r.eligible && r.reasons[0] ? `: ${r.reasons[0]}` : r.would_autobook ? " (Automatik aus)" : ""}
    </span>
  );
}

export function useInboxRouting(companyId) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { const r = await apiClient.get(`/companies/${companyId}/inbox-routing`); setData(r.data); } catch { /* ignore */ }
  }, [companyId]);
  useEffect(() => { load(); }, [load]);
  const save = async (patch) => {
    try {
      const r = await apiClient.put(`/companies/${companyId}/inbox-routing`, patch);
      setData(r.data);
      toast.success(patch.auto_book_enabled === true ? "Auto-Buchung EIN – nur bei Ampel grün, Konfidenz ≥ Schwelle, Betrag ≤ Limit, fester Kontoregel" : patch.auto_book_enabled === false ? "Auto-Buchung AUS – alle Belege gehen in die Freigabe-Queue" : "Routing-Einstellungen gespeichert");
      return true;
    } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); return false; }
  };
  return { data, reload: load, save };
}

/** Schalter + Schatten-Statistik; mode="inbox" kompakt, mode="settings" mit Limits. */
export function InboxRoutingPanel({ companyId, mode = "inbox", onBulkDone }) {
  const { data, save, reload } = useInboxRouting(companyId);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [limits, setLimits] = useState(null);
  useEffect(() => { if (data && !limits) setLimits({ min_confidence: Math.round(data.config.min_confidence * 100), max_amount: data.config.max_amount }); }, [data, limits]);
  if (!data) return null;
  const { config, stats } = data;

  const bulk = async () => {
    setBulkBusy(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/inbox/bulk-confirm`, {});
      toast.success(`${r.data.count} Beleg(e) verbucht${r.data.failed.length ? ` · ${r.data.failed.length} fehlgeschlagen` : ""}`);
      await reload();
      onBulkDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk-Freigabe fehlgeschlagen"); } finally { setBulkBusy(false); }
  };

  return (
    <div className={`rounded-lg border p-3 space-y-2 ${config.auto_book_enabled ? "border-emerald-300 bg-emerald-50/60" : "border-slate-200 bg-slate-50"}`} data-testid="inbox-routing-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Switch checked={config.auto_book_enabled} onCheckedChange={(v) => save({ auto_book_enabled: v })} data-testid="inbox-routing-toggle" />
          <div>
            <div className="text-sm font-medium text-slate-900 flex items-center gap-1.5">
              <Zap className="h-4 w-4 text-amber-500" /> Auto-Buchung {config.auto_book_enabled ? "EIN" : "AUS (manuell)"}
            </div>
            <div className="text-[11px] text-slate-500">
              Automatisch nur bei: Ampel grün · Document AI ≥ {Math.round(config.min_confidence * 100)} % · Betrag ≤ CHF {config.max_amount} · Lieferant bekannt · Konto per fester Regel · nur Belege (Eingangsrechnungen immer manuell)
            </div>
          </div>
        </div>
        {mode === "inbox" && (
          <Button size="sm" onClick={bulk} disabled={bulkBusy || !stats.pending_ready} className="bg-emerald-600 hover:bg-emerald-700" data-testid="inbox-bulk-confirm">
            {bulkBusy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5 mr-1" />} Alle grünen verbuchen ({stats.pending_ready})
          </Button>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2 text-[11px]" data-testid="inbox-routing-stats">
        <span className="rounded border border-emerald-300 bg-white px-1.5 py-px text-emerald-800">Bereit {stats.pending_ready}</span>
        <span className="rounded border border-amber-300 bg-white px-1.5 py-px text-amber-800">Prüfen {stats.pending_review}</span>
        <span className="rounded border border-rose-300 bg-white px-1.5 py-px text-rose-800">Blockiert {stats.pending_blocked}</span>
        <span className="text-slate-500">· letzte {stats.days} Tage: {stats.eligible} Auto-Kandidaten, {stats.shadow_unchanged}/{stats.shadow_confirmed} unverändert bestätigt{stats.autobooked ? `, ${stats.autobooked} automatisch verbucht` : ""}</span>
      </div>
      <div className="flex items-start gap-1.5 text-[11px] text-slate-600" data-testid="inbox-routing-recommendation">
        <Info className="h-3.5 w-3.5 mt-px flex-shrink-0 text-sky-600" /> {stats.recommendation}
      </div>
      {mode === "settings" && limits && (
        <div className="flex flex-wrap items-end gap-3 pt-1 border-t border-dashed border-slate-200">
          <label className="text-[11px] text-slate-600">Konfidenz-Schwelle (%)
            <Input type="number" min={50} max={100} value={limits.min_confidence} onChange={(e) => setLimits({ ...limits, min_confidence: e.target.value })} className="h-8 w-28 text-xs mt-0.5" data-testid="routing-min-confidence" />
          </label>
          <label className="text-[11px] text-slate-600">Auto-Limit (CHF)
            <Input type="number" min={1} step={50} value={limits.max_amount} onChange={(e) => setLimits({ ...limits, max_amount: e.target.value })} className="h-8 w-28 text-xs mt-0.5" data-testid="routing-max-amount" />
          </label>
          <Button size="sm" variant="outline" onClick={() => save({ min_confidence: Number(limits.min_confidence) / 100, max_amount: Number(limits.max_amount) })} data-testid="routing-save-limits">Limits speichern</Button>
        </div>
      )}
    </div>
  );
}
